import { useState, useRef, useEffect } from 'react';
import { Menu, Search, Bell, User, LogOut, Check } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { useDashboardData } from '../../hooks/useDashboardData';
import { Logo } from '../Logo';
import './Header.css';

interface HeaderProps {
  onMenuToggle: () => void;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { organizations, sites, units, changeActiveOrganization } = useDashboardData();
  
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const notifRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (id: string, unitId: string) => {
    markAsRead(id);
    setShowNotifications(false);
    if (unitId) {
      navigate(`/dashboard/units/${unitId}`);
    }
  };

  const filteredOrgs = searchQuery
    ? organizations.filter(org => org.name.toLowerCase().includes(searchQuery.toLowerCase()) && org.role !== 'SiteUser')
    : [];

  const filteredSites = searchQuery
    ? sites.filter(site => site.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  const filteredUnits = searchQuery
    ? units.filter(
        unit =>
          unit.nodeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          unit.unitId.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const hasResults = filteredOrgs.length > 0 || filteredSites.length > 0 || filteredUnits.length > 0;

  const getSiteName = (siteId: string) => {
    const s = sites.find(site => site.id === siteId);
    return s ? s.name : 'Unknown Site';
  };

  return (
    <header className="header">
      <div className="header__left">
        <button
          className="header__menu-btn"
          onClick={onMenuToggle}
          aria-label="Toggle sidebar"
        >
          <Menu size={20} />
        </button>
        <Link to="/dashboard" className="header__brand">
          <Logo className="header__logo-mark" />
          <span className="header__logo">MothNode Dashboard</span>
        </Link>
      </div>

      <div className="header__center">
        <div className="header__search" ref={searchRef}>
          <Search size={16} className="header__search-icon" />
          <input
            type="text"
            className="header__search-input"
            placeholder="Search organizations, sites, units..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearchResults(true);
            }}
            onFocus={() => setShowSearchResults(true)}
          />
          {showSearchResults && searchQuery && (
            <div className="search-results-dropdown">
              {hasResults ? (
                <div className="search-results-list">
                  {filteredOrgs.length > 0 && (
                    <div className="search-results-group">
                      <div className="search-results-group-title">Organizations</div>
                      {filteredOrgs.map(org => (
                        <div
                          key={org.tenantId}
                          className="search-result-item"
                          onClick={() => {
                            changeActiveOrganization(org.tenantId);
                            navigate('/dashboard/organizations');
                            setSearchQuery('');
                            setShowSearchResults(false);
                          }}
                        >
                          <span className="search-result-name">{org.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {filteredSites.length > 0 && (
                    <div className="search-results-group">
                      <div className="search-results-group-title">Sites</div>
                      {filteredSites.map(site => (
                        <div
                          key={site.id}
                          className="search-result-item"
                          onClick={() => {
                            navigate(`/dashboard/sites?siteId=${site.id}`);
                            setSearchQuery('');
                            setShowSearchResults(false);
                          }}
                        >
                          <span className="search-result-name">{site.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {filteredUnits.length > 0 && (
                    <div className="search-results-group">
                      <div className="search-results-group-title">Units</div>
                      {filteredUnits.map(unit => (
                        <div
                          key={unit.unitId}
                          className="search-result-item search-result-item--unit"
                          onClick={() => {
                            navigate(`/dashboard/units/${unit.unitId}`);
                            setSearchQuery('');
                            setShowSearchResults(false);
                          }}
                        >
                          <span className="search-result-name">{unit.nodeName}</span>
                          <span className="search-result-site-tag">
                            {getSiteName(unit.siteId)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="search-results-empty">No results found</div>
              )}
            </div>
          )}
        </div>
      </div>


      <div className="header__right">
        <div className="notification-wrapper" ref={notifRef}>
          <button 
            className="header__icon-btn" 
            aria-label="Notifications"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="header__badge">{unreadCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                {unreadCount > 0 && (
                  <button className="mark-all-btn" onClick={markAllAsRead}>
                    <Check size={14} /> Mark all read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">No notifications</div>
                ) : (
                  notifications.map(notif => (
                    <div 
                      key={notif.id} 
                      className={`notification-item ${!notif.read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notif.id, notif.unitId)}
                    >
                      <div className="notification-content">
                        <p className="notification-message">{notif.message}</p>
                        <span className="notification-time">
                          {new Date(notif.createdAt).toLocaleString()}
                        </span>
                      </div>
                      {!notif.read && <div className="notification-dot" />}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="header__user-menu">
          <Link to="/dashboard/settings" className="header__avatar-link" title="User Settings">
            <div className="header__avatar">
              <User size={20} />
            </div>
          </Link>
          {user && (
            <span className="header__user-email">{user.email}</span>
          )}
        </div>

        <button 
          className="header__icon-btn" 
          aria-label="Sign Out"
          onClick={() => logout()}
          title="Sign Out"
        >
          <LogOut size={20} />
        </button>
      </div>
    </header>
  );
}

export default Header;
